import React, { useState } from 'react';
import { Phone, Envelope, User, X, AddressBook } from '@phosphor-icons/react';

/**
 * "Contact Producer" button + modal shown on ShowDetail.
 * Renders only when the show has at least one producer contact field.
 * Riders click the button to see name / email (tappable mailto) and any
 * phone numbers (tappable tel: links on mobile).
 */
export const ContactProducerButton = ({ show, compact = false }) => {
  const [open, setOpen] = useState(false);

  const name = show?.producer_contact_name;
  const email = show?.producer_contact_email;
  const phones = (show?.producer_contact_phones || []).filter(Boolean);
  const hasAny = name || email || phones.length > 0;

  if (!hasAny) return null;

  const buttonClass = compact
    ? 'flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-[#FF9500]/10 border border-[#FF9500]/30 text-[#FF9500] rounded-lg hover:bg-[#FF9500]/20 transition-colors'
    : 'w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-3.5 bg-[#FF9500]/10 border border-[#FF9500]/30 hover:bg-[#FF9500]/20 text-[#FF9500] font-bold rounded-lg transition-colors text-sm sm:text-base inline-flex items-center justify-center gap-2';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={buttonClass}
        data-testid="contact-producer-btn"
      >
        <AddressBook size={compact ? 12 : 18} weight={compact ? 'bold' : 'duotone'} />
        {compact ? 'Contact' : 'Contact Producer'}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="contact-producer-modal"
        >
          <div
            className="bg-[#111] border border-[#262626] rounded-xl p-6 w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                Contact Producer
              </h3>
              <button
                onClick={() => setOpen(false)}
                className="text-[#9E9E9E] hover:text-white transition-colors"
                data-testid="contact-producer-close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            <p className="text-xs text-[#9E9E9E] mb-5">
              Questions about entries, stalls, scheduling, or refunds? Reach out to the show
              producer directly — faster than filing a dispute.
            </p>

            <div className="space-y-3">
              {name && (
                <div className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] rounded-md px-3 py-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#0096FF]/15 flex items-center justify-center flex-shrink-0">
                    <User size={14} weight="duotone" className="text-[#0096FF]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-semibold">
                      Name
                    </p>
                    <p className="text-sm text-white font-semibold truncate">{name}</p>
                  </div>
                </div>
              )}

              {email && (
                <a
                  href={`mailto:${email}`}
                  className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] hover:border-[#0096FF]/50 rounded-md px-3 py-2.5 transition-colors"
                  data-testid="contact-producer-email"
                >
                  <div className="w-8 h-8 rounded-full bg-[#0096FF]/15 flex items-center justify-center flex-shrink-0">
                    <Envelope size={14} weight="duotone" className="text-[#0096FF]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-semibold">
                      Email
                    </p>
                    <p className="text-sm text-[#0096FF] font-semibold truncate">{email}</p>
                  </div>
                </a>
              )}

              {phones.map((phone, idx) => (
                <a
                  key={idx}
                  href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                  className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] hover:border-[#34C759]/50 rounded-md px-3 py-2.5 transition-colors"
                  data-testid={`contact-producer-phone-${idx}`}
                >
                  <div className="w-8 h-8 rounded-full bg-[#34C759]/15 flex items-center justify-center flex-shrink-0">
                    <Phone size={14} weight="duotone" className="text-[#34C759]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-semibold">
                      {idx === 0 ? 'Phone' : `Alt ${idx + 1}`}
                    </p>
                    <p className="text-sm text-white font-semibold truncate">{phone}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
